import type { ReactNode } from 'react'
import { IconCheck, IconFileText, IconHistory, IconNotebook, IconFileCheck } from '@tabler/icons-react'

import { BrandMark } from '@/components/common/BrandMark'
import { tr } from '@/i18n/tr'
import './auth.css'

/**
 * Giriş ve parola ekranlarının ortak düzeni: solda markanın koyu yüzü, sağda form.
 * Dar ekranda sol bölüm gizlenir; marka formun üstünde tekrar eder.
 */
export function AuthShell({ aside, asideLabel, children }: { aside: ReactNode; asideLabel: string; children: ReactNode }) {
  return (
    <div className="nx-auth">
      <section className="nx-auth-aside" aria-label={asideLabel}>
        <div className="nx-auth-logo"><BrandMark />{tr.app.name}</div>
        {aside}
      </section>
      <main className="nx-auth-main">{children}</main>
    </div>
  )
}

/** Dar ekranda formun üstündeki marka satırı. */
export function AuthFormBrand() {
  return <div className="nx-auth-form__brand"><BrandMark />{tr.app.name}</div>
}

const FEATURES = [
  { icon: IconNotebook, title: 'Proje bazlı notlar', detail: 'Toplantı notları, kontrol listeleri, karar kayıtları.' },
  { icon: IconFileCheck, title: 'Tarayıcıda doküman düzenleme', detail: 'Word, Excel ve sunum dosyaları indirmeden açılır.' },
  { icon: IconHistory, title: 'Sürüm geçmişi', detail: 'Her kayıt saklanır; istediğiniz sürüme geri dönülür.' },
]

/** Giriş ekranının tanıtımı: başlık, ürünü anlatan küçük kompozisyon, üç özellik. */
export function LoginAside() {
  const ic = { size: 14, stroke: 1.8 }
  return (
    <>
      <div className="nx-auth-hero">
        <span className="nx-auth-eyebrow">Kurum içi proje arşivi</span>
        <h2>Projenin notu ve dosyası <em>aynı yerde.</em></h2>
        <p>Not alın, Word ve Excel dosyalarını tarayıcıda düzenleyin, her değişikliğin sürümünü saklayın. Dosyalar kurum sunucusunda kalır.</p>
      </div>

      <div className="nx-auth-demo" aria-hidden="true">
        <div className="nx-auth-card nx-auth-card--note">
          <div className="nx-auth-card__meta"><IconFileText {...ic} />Şubat Raporları · Not</div>
          <div className="nx-auth-card__title">Yönetim kurulu toplantısı</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <DemoCheck done>Bütçe taslağını paylaş</DemoCheck>
            <DemoCheck done>Şube açılış tarihini netleştir</DemoCheck>
            <DemoCheck>Tedarikçi sözleşmesini incele</DemoCheck>
          </div>
        </div>
        <div className="nx-auth-card nx-auth-card--file">
          <span className="nx-auth-file__ext">X</span>
          <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <span className="nx-auth-file__name">Bütçe_2027_taslak.xlsx</span>
            <span className="nx-auth-file__hint">Tarayıcıda düzenleniyor</span>
          </div>
          <span className="nx-auth-pill">v7</span>
        </div>
        <div className="nx-auth-card nx-auth-card--versions">
          <div className="nx-auth-card__meta"><IconHistory {...ic} />Sürümler</div>
          <div className="nx-auth-ver nx-auth-ver--now">
            <span className="nx-auth-ver__dot" /><span className="nx-auth-mono">v7</span>
            <span style={{ color: '#a8aebb' }}>bugün 14:20</span><span className="nx-auth-ver__tag">güncel</span>
          </div>
          <div className="nx-auth-ver"><span className="nx-auth-ver__dot" /><span className="nx-auth-mono">v6</span><span>dün 17:05</span></div>
        </div>
      </div>

      <div className="nx-auth-features">
        {FEATURES.map((f) => (
          <div key={f.title} className="nx-auth-feature">
            <f.icon size={20} stroke={1.8} aria-hidden="true" />
            <strong>{f.title}</strong>
            <span>{f.detail}</span>
          </div>
        ))}
      </div>
    </>
  )
}

function DemoCheck({ done, children }: { done?: boolean; children: ReactNode }) {
  return (
    <div className={done ? 'nx-auth-check nx-auth-check--done' : 'nx-auth-check'}>
      <span className="nx-auth-check__box">{done && <IconCheck size={11} stroke={3} />}</span>
      <span>{children}</span>
    </div>
  )
}

export type StepState = 'done' | 'now' | 'todo'

/** Parola ekranının tanıtımı: başlık ve akışın adımları. */
export function StepsAside({ steps }: { steps: { label: string; hint: string; state: StepState }[] }) {
  return (
    <>
      <div className="nx-auth-hero" style={{ gap: 32, maxWidth: 520 }}>
        <h2 style={{ fontSize: 'clamp(36px, 3.4vw, 46px)', lineHeight: 1.05 }}>Hesabınızı <em>kendinize ait</em> bir parolayla koruyun.</h2>
        <ol className="nx-auth-steps">
          {steps.map((s, i) => (
            <li key={s.label} className="nx-auth-step" data-state={s.state} aria-current={s.state === 'now' ? 'step' : undefined}>
              <div className="nx-auth-step__rail">
                <span className="nx-auth-step__dot">{s.state === 'done' ? <IconCheck size={14} stroke={2.6} aria-label="Tamamlandı" /> : i + 1}</span>
                <span className="nx-auth-step__line" />
              </div>
              <div className="nx-auth-step__text"><strong>{s.label}</strong><span>{s.hint}</span></div>
            </li>
          ))}
        </ol>
      </div>
      <span className="nx-auth-aside__foot">Parolanızı değiştirdiğinizde tüm oturumlarınız güvenlik için sonlandırılır.</span>
    </>
  )
}
